import { useState, createContext, useContext, useEffect } from "react";
import {
  MoreVertical,
  Moon,
  Sun,
  UserCircle,
  Users,
  Home,
  Package,
  ShoppingCart,
  Users as UsersIcon,
  Database,
  Factory,
  TrendingUp,
  DollarSign,
  Settings as SettingsIcon,
} from "lucide-react";
import { SidebarProvider, SidebarInset } from "@/components/ui/sidebar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { PageRouter } from "@/components/page-router";
import { GlobalModeToggle } from "@/components/global-mode-toggle";
import { UserSwitcher } from "@/components/user-switcher";
import { GlobalSearch } from "@/components/global-search";
import { usePermissions } from "@/providers/permissions-provider";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Switch } from "@/components/ui/switch";
import { useTheme } from "next-themes";
import { LeftMenu } from "@/components/LeftMenu";
import { Breadcrumb, BreadcrumbItem } from "@/components/ui/breadcrumb";
import { OnboardingWizard } from "@/components/onboarding/OnboardingWizard";
import { useWarehouseHierarchyConfig } from "@/hooks/use-warehouse-hierarchy-config";
import { useMode } from "@/providers/ModeProvider";

interface NavigationContextType {
  activePage: string;
  pageParams: Record<string, any>;
  navigateTo: (page: string, params?: Record<string, any>) => void;
}

const NavigationContext = createContext<NavigationContextType | undefined>(undefined);

export const useNavigation = () => {
  const context = useContext(NavigationContext);
  if (!context) {
    throw new Error("useNavigation must be used within AppLayout");
  }
  return context;
};

export function AppLayout() {
  const [activePage, setActivePage] = useState("dashboard");
  const [pageParams, setPageParams] = useState<Record<string, any>>({});
  const [databaseInstance, setDatabaseInstance] = useState("production");
  const { currentUser, currentProfile } = usePermissions();
  const { theme, setTheme } = useTheme();
  const [isUserSwitcherOpen, setIsUserSwitcherOpen] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState("production");

  // Watch for "New Company" selection to trigger onboarding
  useEffect(() => {
    if (selectedCompany === "new-company") {
      setShowOnboarding(true);
    }
  }, [selectedCompany]);

  const handleOnboardingComplete = () => {
    setShowOnboarding(false);
    // Reset to a default company after onboarding
    setSelectedCompany("production");
    // Navigate to dashboard
    handleNavigation("dashboard");
  };

  // Get the mode for warehouse-location-setup screen to determine if we should fetch from API
  const { isScreenLive } = useMode();
  const isWarehouseSetupLive = isScreenLive("warehouse-location-setup");

  // Get level 1 label from warehouse hierarchy config for breadcrumbs
  const { getLevelDisplayName } = useWarehouseHierarchyConfig({ isLive: isWarehouseSetupLive });
  const level1Label = getLevelDisplayName(1);

  const handleNavigation = (pageId: string, params?: Record<string, any>) => {
    setActivePage(pageId);
    setPageParams(params || {});
  };

  const getBreadcrumbs = (): BreadcrumbItem[] => {
    // Define page metadata with parent relationships based on actual navigation flow
    // Parent should be the actual page you navigate FROM, not the menu section
    const pageMetadata: Record<string, { label: string; parent?: string; icon?: React.ReactNode }> = {
      // Root
      "dashboard": { label: "Home", icon: <Home className="h-4 w-4" /> },
      
      // Main Section
      "goods-in-out": { label: "Goods In / Out", icon: <Package className="h-4 w-4" /> },
      "stock-assistant": { label: "Stock Assistant", icon: <Package className="h-4 w-4" /> },
      "quick-start": { label: "Quick Start", icon: <Package className="h-4 w-4" /> },
      
      // Inventory Section
      "stock-items": { label: "Stock Items", icon: <Package className="h-4 w-4" /> },
      "non-stock-items": { label: "Non-Stock Items", icon: <Package className="h-4 w-4" /> },
      "warehouses": { label: level1Label, icon: <Package className="h-4 w-4" /> },
      "stock-transfers": { label: "Stock Transfers", icon: <Package className="h-4 w-4" /> },
      "add-movement": { label: "Stock Movements", icon: <Package className="h-4 w-4" /> },
      "stock-movements": { label: "Stock Movement History", icon: <Package className="h-4 w-4" /> },
      "stock-takes": { label: "Stock Takes", icon: <Package className="h-4 w-4" /> },
      "tracking": { label: "Lot/Batch/Serial Tracking", icon: <Package className="h-4 w-4" /> },
      "abc-classification": { label: "ABC Classification", icon: <TrendingUp className="h-4 w-4" /> },
      
      // Purchasing Section
      "purchase-orders": { label: "Purchase Orders", icon: <ShoppingCart className="h-4 w-4" /> },
      "receive-goods": { label: "Receive Goods", icon: <ShoppingCart className="h-4 w-4" /> },
      "suppliers": { label: "Suppliers", icon: <UsersIcon className="h-4 w-4" /> },

      // Sales & Fulfillment Section
      "sales-orders": { label: "Sales Orders", icon: <ShoppingCart className="h-4 w-4" /> },
      "pick-pack": { label: "Pick & Pack", icon: <ShoppingCart className="h-4 w-4" /> },
      "shipping": { label: "Shipping", icon: <ShoppingCart className="h-4 w-4" /> },
      "customers": { label: "Customers", icon: <UsersIcon className="h-4 w-4" /> },

      // Master Data Section
      "projects-cost-centers": { label: "Projects & Cost Centers", icon: <Database className="h-4 w-4" /> },

      // Manufacturing Section
      "boms": { label: "Bills of Material (BOMs)", icon: <Factory className="h-4 w-4" /> },
      "work-orders": { label: "Work Orders", icon: <Factory className="h-4 w-4" /> },
      "production-schedule": { label: "Production Schedule", icon: <Factory className="h-4 w-4" /> },
      "routing": { label: "Routings & Work Centers", icon: <Factory className="h-4 w-4" /> },

      // Analytics & Forecasting Section
      "ai-forecasting": { label: "Demand Forecasting", icon: <TrendingUp className="h-4 w-4" /> },
      "reorder": { label: "Reorder Recommendations", icon: <TrendingUp className="h-4 w-4" /> },
      "margin-analysis": { label: "Margin Analysis", icon: <DollarSign className="h-4 w-4" /> },
      "cost-analysis": { label: "Cost Analysis", icon: <DollarSign className="h-4 w-4" /> },

      // Pricing Section
      "price-matrix": { label: "Price Matrix", icon: <DollarSign className="h-4 w-4" /> },
      "advanced-pricing": { label: "Advanced Pricing", icon: <DollarSign className="h-4 w-4" /> },

      // Main landing pages - Settings Section
      "stock-options": { label: "System Configuration", icon: <SettingsIcon className="h-4 w-4" /> },
      "integration-sync": { label: "Integration", icon: <SettingsIcon className="h-4 w-4" /> },
      "company-settings": { label: "Company Settings", icon: <SettingsIcon className="h-4 w-4" /> },
      "warehouse-location-setup": { label: level1Label, icon: <SettingsIcon className="h-4 w-4" /> },
      "user-management": { label: "User Management", icon: <SettingsIcon className="h-4 w-4" /> },
      "custom-fields": { label: "Custom Fields", icon: <SettingsIcon className="h-4 w-4" /> },
      "fifo-demo": { label: "FIFO Demo", icon: <SettingsIcon className="h-4 w-4" /> },
      "permissions-demo": { label: "Permissions Demo", icon: <SettingsIcon className="h-4 w-4" /> },
      "help-system-demo": { label: "Help System Demo", icon: <SettingsIcon className="h-4 w-4" /> },

      // Sub-pages (navigated to FROM main landing pages)
      // Example: Dashboard > Stock Items > Create New Stock Item
      "create-stock-item": { label: "Create New Stock Item", parent: "stock-items" },
      "stock-item-detail": { label: "Stock Item Details", parent: "stock-items" },
      "create-bom": { label: "Create New BOM", parent: "bills-of-material" },
      "create-work-order": { label: "Create New Work Order", parent: "work-orders" },

      // Example: Dashboard > Stock Movements > Transfer Between Locations
      "add-movement": { label: "Create Stock Movement", parent: "stock-movements" },
      "new-stock-movement": { label: "New Stock Movement", parent: "stock-movements" },
      "transfer-movement": { label: "Transfer Between Locations", parent: "stock-movements" },
      "sales-movement": { label: "Sales Movement", parent: "stock-movements" },
      "purchase-movement": { label: "Purchase Movement", parent: "stock-movements" },
      "write-off-movement": { label: "Write-off Movement", parent: "stock-movements" },
      "unified-stock-movement": { label: "Unified Stock Movement", parent: "stock-movements" },

      // Stock Takes sub-pages
      "stock-takes-home": { label: "Stock Takes Home", parent: "stock-takes" },
      "create-stock-take": { label: "Create Stock Take", parent: "stock-takes" },
      "stock-takes/quick-count/setup": { label: "Quick Count Setup", parent: "stock-takes" },
      "stock-takes/quick-count/count": { label: "Quick Count", parent: "stock-takes" },
      "stock-takes/quick-count/review": { label: "Quick Count Review", parent: "stock-takes" },
      "stock-takes/targeted-count/setup": { label: "Targeted Count Setup", parent: "stock-takes" },
      "stock-takes/full/setup": { label: "Full Count Setup", parent: "stock-takes" },
      "stock-takes/full/count": { label: "Full Count", parent: "stock-takes" },
      "stock-takes/full/review": { label: "Full Count Review", parent: "stock-takes" },
      "stock-takes/targeted/setup": { label: "Targeted Count Setup", parent: "stock-takes" },
      "stock-takes/targeted/count": { label: "Targeted Count", parent: "stock-takes" },
      "stock-takes/targeted/review": { label: "Targeted Count Review", parent: "stock-takes" },
      "stock-takes/cycle-count/setup": { label: "Cycle Count Setup", parent: "stock-takes" },
      "stock-takes/cycle-count/count": { label: "Cycle Count", parent: "stock-takes" },
      "stock-takes/cycle-count/review": { label: "Cycle Count Review", parent: "stock-takes" },

      // Stock Transfers sub-pages
      "stock-transfers/create/step1": { label: "Select Items", parent: "stock-transfers" },
      "stock-transfers/create/step2": { label: "Configure Transfers", parent: "stock-transfers" },
      "stock-transfers/create/step3": { label: "Review & Complete", parent: "stock-transfers" },

      // Other sub-pages
      assembly: { label: "Assembly", parent: "boms" },
      reports: { label: "Reports", parent: "dashboard" },
    };

    const breadcrumbs: BreadcrumbItem[] = [];
    const currentPage = pageMetadata[activePage];

    // Always start with Dashboard (unless we're on Dashboard)
    if (activePage !== "dashboard") {
      breadcrumbs.push({
        label: "Dashboard",
        icon: <Home className="h-4 w-4" />,
        onClick: (e) => {
          e.preventDefault();
          handleNavigation("dashboard");
        },
      });
    }

    if (!currentPage) {
      // If page not found, just show Dashboard
      if (activePage === "dashboard") {
        breadcrumbs.push({
          label: "Dashboard",
          icon: <Home className="h-4 w-4" />,
        });
      }
      return breadcrumbs;
    }

    // Build breadcrumb trail recursively
    const buildTrail = (pageId: string): Array<{ label: string; page: string; icon?: React.ReactNode }> => {
      const page = pageMetadata[pageId];
      if (!page) return [];

      const trail: Array<{ label: string; page: string; icon?: React.ReactNode }> = [];

      // If this page has a parent, recurse to build the full trail
      if (page.parent) {
        const parentPage = pageMetadata[page.parent];
        if (parentPage) {
          // Add parent's trail first
          trail.push(...buildTrail(page.parent));
          // Then add the parent itself
          trail.push({ label: parentPage.label, page: page.parent, icon: parentPage.icon });
        }
      }

      return trail;
    };

    // Add parent trail (all clickable)
    const trail = buildTrail(activePage);
    trail.forEach((item) => {
      breadcrumbs.push({
        label: item.label,
        icon: item.page === "dashboard" ? item.icon : undefined, // Only show icon for Dashboard
        onClick: (e) => {
          e.preventDefault();
          handleNavigation(item.page);
        },
      });
    });

    // Add current page (not clickable)
    breadcrumbs.push({
      label: currentPage.label,
      icon: activePage === "dashboard" ? currentPage.icon : undefined, // Only show icon for Dashboard
    });

    return breadcrumbs;
  };

  return (
    <NavigationContext.Provider value={{ activePage, pageParams, navigateTo: handleNavigation }}>
    <SidebarProvider>
      <LeftMenu 
        activePage={activePage} 
        onPageChange={handleNavigation}
        selectedCompany={selectedCompany}
        setSelectedCompany={setSelectedCompany}
      />

        <SidebarInset className="overflow-x-hidden">
          <header className="sticky top-0 z-50 flex h-16 items-center gap-4 border-b bg-background px-4 lg:px-6">
            <div className="flex items-center justify-between flex-1 gap-4">
              <Breadcrumb items={getBreadcrumbs()} />
              <div className="flex items-center gap-2">
                <GlobalSearch />
                <GlobalModeToggle />
                {currentUser && (
                  <>
                    <Separator orientation="vertical" className="h-6" />
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm" className="gap-2">
                          <Users className="h-4 w-4" />
                          <span className="hidden md:inline">{currentUser.name}</span>
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-56">
                        <div className="px-2 py-1.5 text-sm font-normal">
                          <div className="flex flex-col space-y-1">
                            <p className="text-sm font-medium leading-none">{currentUser.name}</p>
                            <p className="text-xs leading-none text-muted-foreground">{currentProfile?.name}</p>
                          </div>
                        </div>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => setIsUserSwitcherOpen(true)}>
                          <UserCircle className="mr-2 h-4 w-4" />
                          <span>Switch User</span>
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onClick={(e) => {
                            e.preventDefault();
                            setTheme(theme === "dark" ? "light" : "dark");
                          }}
                          className="justify-between"
                        >
                          <div className="flex items-center">
                            <Sun className="mr-2 h-4 w-4 rotate-0 scale-100 transition-all dark:-rotate-90 dark:scale-0" />
                            <Moon className="absolute mr-2 h-4 w-4 rotate-90 scale-0 transition-all dark:rotate-0 dark:scale-100" />
                            <span className="ml-6">Theme</span>
                          </div>
                          <Switch
                            checked={theme === "dark"}
                            onCheckedChange={(checked) => setTheme(checked ? "dark" : "light")}
                            onClick={(e) => e.stopPropagation()}
                          />
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </>
                )}
              </div>
            </div>
          </header>

        <main className="flex-1 p-4 overflow-x-hidden">
          <PageRouter activePage={activePage} pageParams={pageParams} />
        </main>
      </SidebarInset>
      <UserSwitcher open={isUserSwitcherOpen} onOpenChange={setIsUserSwitcherOpen} />
      <OnboardingWizard open={showOnboarding} onComplete={handleOnboardingComplete} />
    </SidebarProvider>
    </NavigationContext.Provider>
  );
}
